import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, ShieldCheck, Sparkles, CheckCircle2, Quote } from "lucide-react";
import { Section, SectionTitle } from "../components/SectionTitle";
import ServiceCard from "../components/ServiceCard";
import ConsultationForm from "../components/ConsultationForm";
import SEO from "../components/SEO";
import { CalendlyButton } from "../components/Calendly";
import { CORE_SERVICES, WHO_WE_SERVE, WHY_CHOOSE, PROCESS_STEPS, BUNDLES, TESTIMONIALS } from "../lib/serviceData";

const HERO_BG = "https://images.unsplash.com/photo-1475503572774-15a45e5d60b9?auto=format&fit=crop&w=2000&q=80";

export default function Home() {
  return (
    <div className="bg-white">
      <SEO
        title="Financial, Tax, Insurance & Legal Services USA"
        description="HSA Service Hub helps individuals, families, and businesses across the United States protect their future, manage responsibilities, and grow with confidence."
        keywords="financial services USA, tax services USA, insurance services USA, legal services coordination, family financial planning, business tax services, estate planning support, life insurance planning, small business financial services, financial tax insurance legal services"
        path="/"
      />
      {/* HERO */}
      <section className="relative bg-[#0a2540] overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a2540] via-[#0a2540]/85 to-[#0a2540]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-32 lg:py-40">
          <div className="max-w-3xl fade-in-up">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-1.5 mb-6 backdrop-blur">
              <ShieldCheck className="w-4 h-4 text-[#d4af37]" />
              <span className="text-xs uppercase tracking-[0.2em] text-white/90 font-semibold">Trusted USA Service Hub</span>
            </div>
            <h1 className="font-serif-hsa text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-6">
              Your Trusted Partner for Financial, Tax, Insurance &amp; <span className="text-[#d4af37]">Legal Services</span>
            </h1>
            <p className="text-lg text-white/85 leading-relaxed mb-10 max-w-2xl">
              HSA Service Hub helps individuals, families, and businesses across the United States
              protect their future, manage responsibilities, and grow with confidence.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact">
                <Button data-testid="hero-book-consultation-btn" size="lg" className="bg-[#d4af37] hover:bg-[#b8951f] text-[#0a2540] font-semibold rounded-full px-7 h-12 text-base">
                  Book a Consultation <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/services">
                <Button data-testid="hero-explore-services-btn" size="lg" variant="outline" className="rounded-full px-7 h-12 text-base bg-transparent border-white/40 text-white hover:bg-white hover:text-[#0a2540]">
                  Explore Our Services
                </Button>
              </Link>
              <Link to="/contact">
                <Button data-testid="hero-speak-advisor-btn" size="lg" variant="ghost" className="rounded-full px-7 h-12 text-base text-white hover:bg-white/10">
                  Speak With an Advisor
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-6 mt-14 max-w-xl">
              {[
                ["50", "States Served"],
                ["4", "Core Service Pillars"],
                ["1", "Trusted Hub"],
              ].map(([n, l]) => (
                <div key={l}>
                  <div className="font-serif-hsa text-3xl sm:text-4xl text-[#d4af37] font-bold">{n}</div>
                  <div className="text-xs uppercase tracking-[0.15em] text-white/70 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST INTRO */}
      <Section className="bg-white">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-3">One Umbrella · Endless Possibilities</div>
            <h2 className="font-serif-hsa text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0a2540] leading-[1.1]">
              Comprehensive Support for Life, Business &amp; Long-Term Protection
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-4">
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              At HSA Service Hub, we bring together essential professional services under one trusted
              platform. From tax filing and insurance planning to financial guidance and legal service
              coordination, our goal is to help clients make informed decisions with clarity and confidence.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              {["Coordinated multi-service approach", "Plain-English communication", "USA-based service standards", "Confidential and respectful"].map((t) => (
                <div key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#1e6dd8] mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* CORE SERVICES */}
      <Section className="bg-[#f8f9fa]">
        <SectionTitle
          eyebrow="Core Services"
          title="Four Pillars. One Trusted Partner."
          subtitle="Each service is delivered with the same standard of clarity, transparency, and long-term focus."
        />
        <div className="grid md:grid-cols-2 gap-6">
          {CORE_SERVICES.slice(0, 4).map((s) => (
            <ServiceCard
              key={s.slug}
              icon={s.icon}
              title={s.title}
              description={s.description}
              points={s.points}
              href={`/services/${s.slug}`}
              testId={`home-service-card-${s.slug}`}
            />
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/services">
            <Button data-testid="home-view-all-services-btn" variant="outline" className="border-[#0a2540] text-[#0a2540] hover:bg-[#0a2540] hover:text-white rounded-full px-6 h-11">
              View All Services <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </Section>

      {/* WHO WE SERVE */}
      <Section className="bg-white">
        <SectionTitle
          eyebrow="Who We Serve"
          title="Serving Individuals, Families & Businesses Across the United States"
          subtitle="Whatever life stage or business size, our service hub adapts to your goals."
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {WHO_WE_SERVE.map(({ label, icon: Icon }) => (
            <div key={label} className="lift-card bg-white border border-[#cbd5e1] rounded-xl p-5 text-center">
              <Icon className="w-7 h-7 text-[#1e6dd8] mx-auto mb-3" strokeWidth={1.6} />
              <div className="text-sm font-semibold text-[#0a2540] leading-snug">{label}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* WHY CHOOSE */}
      <Section className="bg-[#0a2540] text-white">
        <SectionTitle
          light
          eyebrow="Why HSA"
          title="Why Clients Choose HSA Service Hub"
          subtitle="Eight reasons clients across the United States trust us with the things that matter most."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {WHY_CHOOSE.map((w) => (
            <div key={w.title} className="border border-white/15 bg-white/[0.03] rounded-xl p-6 hover:border-[#d4af37]/60 transition-colors">
              <Sparkles className="w-5 h-5 text-[#d4af37] mb-4" />
              <h4 className="font-serif-hsa text-lg font-semibold text-white mb-2">{w.title}</h4>
              <p className="text-sm text-white/70 leading-relaxed">{w.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* BUNDLES */}
      <Section className="bg-[#f8f9fa]">
        <SectionTitle
          eyebrow="Service Bundles"
          title="Solutions Built Around Your Needs"
          subtitle="Three coordinated plans that bring our services together for the way you actually live and work."
        />
        <div className="grid md:grid-cols-3 gap-6">
          {BUNDLES.map((b) => (
            <div
              key={b.name}
              className={`relative rounded-2xl p-8 lift-card ${
                b.featured
                  ? "bg-[#0a2540] text-white border-2 border-[#d4af37]"
                  : "bg-white text-slate-800 border border-[#cbd5e1]"
              }`}
            >
              {b.featured && (
                <div className="absolute -top-3 left-8 bg-[#d4af37] text-[#0a2540] text-[11px] font-bold uppercase tracking-[0.15em] px-3 py-1 rounded-full">
                  Most Popular
                </div>
              )}
              <h3 className={`font-serif-hsa text-2xl font-semibold mb-2 ${b.featured ? "text-white" : "text-[#0a2540]"}`}>{b.name}</h3>
              <p className={`text-sm mb-6 ${b.featured ? "text-white/75" : "text-slate-500"}`}>{b.tagline}</p>
              <ul className="space-y-3 mb-8">
                {b.items.map((i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm">
                    <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${b.featured ? "text-[#d4af37]" : "text-[#1e6dd8]"}`} />
                    <span className={b.featured ? "text-white/90" : "text-slate-700"}>{i}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact">
                <Button
                  data-testid={`bundle-cta-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`w-full rounded-full h-11 font-semibold ${
                    b.featured
                      ? "bg-[#d4af37] hover:bg-[#b8951f] text-[#0a2540]"
                      : "bg-[#0a2540] hover:bg-[#1e6dd8] text-white"
                  }`}
                >
                  Request a Custom Consultation
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </Section>

      {/* PROCESS */}
      <Section className="bg-white">
        <SectionTitle
          eyebrow="How It Works"
          title="How We Help You Move Forward"
          subtitle="A clear, seven-step process from first conversation to long-term partnership."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PROCESS_STEPS.map((step, i) => (
            <div key={step} className="relative bg-[#f8f9fa] border border-[#cbd5e1] rounded-xl p-6">
              <div className="font-serif-hsa text-4xl font-bold text-[#d4af37] leading-none mb-3">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h4 className="font-serif-hsa text-lg font-semibold text-[#0a2540] leading-snug">{step}</h4>
            </div>
          ))}
        </div>
      </Section>

      {/* CONFIDENCE BANNER */}
      <section className="bg-gradient-to-r from-[#0a2540] to-[#143a64] text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif-hsa text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mb-5">
            Plan Today. Protect Tomorrow. <span className="text-[#d4af37]">Grow With Confidence.</span>
          </h2>
          <p className="text-lg text-white/85 leading-relaxed">
            Whether you are preparing your taxes, protecting your family, planning your estate, securing
            insurance, or organizing your business responsibilities, HSA Service Hub is here to guide you
            with clarity and care.
          </p>
        </div>
      </section>

      {/* LEAD CAPTURE */}
      <Section className="bg-[#f8f9fa]" id="consultation">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-3">Free Consultation</div>
            <h2 className="font-serif-hsa text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0a2540] leading-[1.1] mb-5">
              Request a Free Initial Consultation
            </h2>
            <p className="text-slate-600 mb-6">
              Tell us a little about your situation and the service you are exploring. An HSA Service
              Hub advisor will reach out within one business day.
            </p>
            <ul className="space-y-3 text-slate-700 text-sm">
              {[
                "No obligation, fully confidential",
                "Coordinated review across all services",
                "Plain English explanations and next steps",
                "USA-based service approach",
              ].map((p) => (
                <li key={p} className="flex items-start gap-2.5"><CheckCircle2 className="w-4 h-4 text-[#1e6dd8] mt-0.5" />{p}</li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <ConsultationForm />
          </div>
        </div>
      </Section>

      {/* TESTIMONIALS */}
      <Section className="bg-white">
        <SectionTitle
          eyebrow="Client Voices"
          title="What HSA Service Hub Clients Say"
          subtitle="Real, anonymized feedback from individuals, families, and small business owners."
          align="center"
        />
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t) => (
            <div key={t.role} className="bg-[#f8f9fa] border border-[#cbd5e1] rounded-2xl p-8 lift-card">
              <Quote className="w-8 h-8 text-[#d4af37] mb-4" strokeWidth={1.4} />
              <p className="text-slate-700 leading-relaxed italic mb-6">"{t.quote}"</p>
              <div className="border-t border-[#cbd5e1] pt-4">
                <div className="font-serif-hsa text-base font-semibold text-[#0a2540]">{t.role}</div>
                <div className="text-sm text-slate-500">{t.location}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* FINAL CTA */}
      <section className="bg-[#0a2540] text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif-hsa text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mb-5">
            Ready to Protect Your Future?
          </h2>
          <p className="text-lg text-white/85 leading-relaxed mb-9 max-w-2xl mx-auto">
            Connect with HSA Service Hub today and take the next step toward financial clarity, tax
            confidence, insurance protection, and legal preparedness.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/contact"><Button data-testid="final-cta-book-btn" className="bg-[#d4af37] hover:bg-[#b8951f] text-[#0a2540] font-semibold rounded-full px-7 h-12">Book a Consultation</Button></Link>
            <CalendlyButton dataTestId="final-cta-calendly-btn" className="bg-white hover:bg-[#f8f9fa] text-[#0a2540] font-semibold rounded-full px-7 h-12">
              Pick a Time Now
            </CalendlyButton>
            <Link to="/services"><Button data-testid="final-cta-services-btn" variant="ghost" className="rounded-full px-7 h-12 text-white hover:bg-white/10">Explore Services</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
